/**
 * AcenteAgencyDetail — Admin/Operasyon için tek acentenin drill-down sayfası.
 *
 * Iter 39: "Acenteler → tıkla → rezervasyonları + ödemeleri + fiyatları"
 * kullanıcı talebi. 3 sekme:
 *   1. Rezervasyonlar — bu acentenin tüm aktif rezervasyonları
 *   2. Ödemeler       — toplam anlaşılan, toplanan, bakiye + tahsilat listesi
 *   3. Fiyatlar       — otel bazlı birim fiyat CRUD; yeni rezervasyon
 *                       eklenirken bu fiyatlar otomatik doldurulur
 */
import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import panelApi from "@/lib/panelApi";
import { formatTr } from "@/lib/date";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  ArrowLeft, Building2, Calendar, Wallet, DollarSign, Eye, Plus, Pencil,
  Trash2, BedDouble, Phone, User, Sparkles, CheckCircle2,
} from "lucide-react";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { numberFieldProps } from "@/lib/numberInput";

const TABS = [
  { key: "reservations", label: "Rezervasyonlar", icon: Calendar },
  { key: "payments", label: "Ödemeler", icon: Wallet },
  { key: "prices", label: "Otel Fiyatları", icon: DollarSign },
];

const fmtSar = (n) => `${Number(n || 0).toLocaleString("tr-TR", { maximumFractionDigits: 2 })} ﷼`;

export default function AcenteAgencyDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const [tab, setTab] = useState("reservations");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [priceForm, setPriceForm] = useState({ hotel_name: "", room_unit_price: 0, meal_unit_price: 0, note: "" });
  const [savingPrice, setSavingPrice] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [editingId, setEditingId] = useState(null);
  // Iter 41: Toplu fiyat modalı — operasyon aynı ekranda birden fazla otel için fiyat ekler.
  const [batchOpen, setBatchOpen] = useState(false);
  const [batchRows, setBatchRows] = useState([
    { hotel_name: "", room_unit_price: 0, meal_unit_price: 0, note: "" },
    { hotel_name: "", room_unit_price: 0, meal_unit_price: 0, note: "" },
    { hotel_name: "", room_unit_price: 0, meal_unit_price: 0, note: "" },
  ]);
  const [batchBusy, setBatchBusy] = useState(false);

  const load = () => {
    setLoading(true);
    panelApi
      .get(`/agencies/${id}/summary`)
      .then((r) => setData(r.data))
      .catch((e) => toast.error(e?.friendly || e?.response?.data?.detail || "Yüklenemedi"))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id]);

  const stats = data?.stats || { reservation_count: 0, total_agreed: 0, total_collected: 0, balance: 0 };
  const reservations = data?.reservations || [];
  const prices = data?.prices || [];
  const agency = data?.agency;

  // Ödemeler sekmesi: rezervasyonlar üzerinden per-row collected_amount listesi
  const paymentRows = useMemo(() => {
    return reservations
      .filter((r) => (r.collected_amount || 0) > 0)
      .map((r) => ({
        id: r.id,
        reservation_number: r.reservation_number,
        group_name: r.group_name || r.customer_phone,
        checkin: r.checkin,
        collected_amount: Number(r.collected_amount || 0),
        collected_by_name: r.collected_by_name || "",
        collected_at: r.collected_at || null,
      }))
      .sort((a, b) => (b.collected_at || "").localeCompare(a.collected_at || ""));
  }, [reservations]);

  const startEditPrice = (p) => {
    setEditingId(p.id);
    setPriceForm({
      hotel_name: p.hotel_name,
      room_unit_price: p.room_unit_price || 0,
      meal_unit_price: p.meal_unit_price || 0,
      note: p.note || "",
    });
    setTab("prices");
    // scroll to form
    setTimeout(() => document.getElementById("agency-price-form")?.scrollIntoView({ behavior: "smooth" }), 100);
  };

  const submitPrice = async () => {
    const name = (priceForm.hotel_name || "").trim();
    if (!name) return toast.error("Otel adı zorunlu");
    setSavingPrice(true);
    try {
      await panelApi.post(`/agencies/${id}/prices`, priceForm);
      toast.success(editingId ? "Fiyat güncellendi" : "Fiyat kaydedildi");
      setPriceForm({ hotel_name: "", room_unit_price: 0, meal_unit_price: 0, note: "" });
      setEditingId(null);
      load();
    } catch (e) {
      toast.error(e?.friendly || e?.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setSavingPrice(false);
    }
  };

  const confirmDeletePrice = async () => {
    if (!deleteTarget) return;
    try {
      await panelApi.delete(`/agencies/${id}/prices/${deleteTarget.id}`);
      toast.success("Fiyat silindi");
      setDeleteTarget(null);
      load();
    } catch (e) {
      toast.error(e?.friendly || e?.response?.data?.detail || "Silinemedi");
    }
  };

  const updateBatchRow = (idx, k, v) => {
    setBatchRows((rows) => rows.map((r, i) => (i === idx ? { ...r, [k]: v } : r)));
  };
  const addBatchRow = () => setBatchRows((rows) => [...rows, { hotel_name: "", room_unit_price: 0, meal_unit_price: 0, note: "" }]);
  const removeBatchRow = (idx) => setBatchRows((rows) => rows.filter((_, i) => i !== idx));

  const submitBatch = async () => {
    const items = batchRows
      .map((r) => ({
        hotel_name: (r.hotel_name || "").trim(),
        room_unit_price: Number(r.room_unit_price || 0),
        meal_unit_price: Number(r.meal_unit_price || 0),
        note: (r.note || "").trim(),
      }))
      .filter((r) => r.hotel_name);
    if (items.length === 0) {
      toast.error("En az bir otel adı girin");
      return;
    }
    setBatchBusy(true);
    try {
      const r = await panelApi.post(`/agencies/${id}/prices/batch`, { items });
      toast.success(`${r?.data?.upserted ?? items.length} otel fiyatı kaydedildi`);
      setBatchOpen(false);
      // Reset for the next time
      setBatchRows([
        { hotel_name: "", room_unit_price: 0, meal_unit_price: 0, note: "" },
        { hotel_name: "", room_unit_price: 0, meal_unit_price: 0, note: "" },
        { hotel_name: "", room_unit_price: 0, meal_unit_price: 0, note: "" },
      ]);
      load();
    } catch (e) {
      toast.error(e?.friendly || e?.response?.data?.detail || "Kaydedilemedi");
    } finally {
      setBatchBusy(false);
    }
  };

  if (loading && !data) {
    return <div className="text-slate-500 py-10 text-center" data-testid="agency-detail-loading">Yükleniyor...</div>;
  }
  if (!agency) {
    return (
      <div className="text-center py-10">
        <div className="text-slate-500">Acente bulunamadı.</div>
        <Link to="/acente/acenteler" className="text-brand-orange text-sm mt-2 inline-block">Acenteler listesine dön</Link>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="agency-detail-page">
      <div className="flex items-start justify-between flex-wrap gap-4">
        <div>
          <button onClick={() => nav("/acente/acenteler")} className="text-sm text-brand-navy/60 hover:text-brand-orange inline-flex items-center gap-1" data-testid="agency-detail-back">
            <ArrowLeft size={14}/> Acenteler
          </button>
          <div className="mt-1 flex items-center gap-2">
            <Building2 size={22} className="text-brand-orange"/>
            <h1 className="font-serif text-3xl text-brand-navy" data-testid="agency-detail-name">{agency.name}</h1>
            {agency.active === false && <span className="text-xs px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">Pasif</span>}
          </div>
          <div className="text-xs text-slate-500 mt-1">
            {agency.contact && <span>{agency.contact} · </span>}
            {agency.phone && <span>📞 {agency.phone} · </span>}
            {agency.tax_id && <span>VKN: {agency.tax_id}</span>}
          </div>
        </div>
      </div>

      {/* KPI cards */}
      <div className="grid gap-3 sm:grid-cols-4">
        <KpiCard icon={<Calendar size={18}/>} label="Rezervasyon" value={stats.reservation_count} tone="navy" testid="agency-kpi-reservations"/>
        <KpiCard icon={<DollarSign size={18}/>} label="Toplam Anlaşılan" value={fmtSar(stats.total_agreed)} tone="orange" testid="agency-kpi-agreed"/>
        <KpiCard icon={<Wallet size={18}/>} label="Toplanan" value={fmtSar(stats.total_collected)} tone="emerald" testid="agency-kpi-collected"/>
        <KpiCard icon={<Wallet size={18}/>} label="Bakiye" value={fmtSar(stats.balance)} tone={stats.balance > 0 ? "rose" : "slate"} testid="agency-kpi-balance"/>
      </div>

      {/* Tabs */}
      <div className="border-b border-brand-navy/10 flex flex-wrap gap-2" data-testid="agency-detail-tabs">
        {TABS.map((t) => {
          const active = tab === t.key;
          const Ico = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => setTab(t.key)}
              className={`px-4 py-2 text-sm font-medium border-b-2 -mb-px inline-flex items-center gap-2 transition ${
                active ? "border-brand-orange text-brand-orange" : "border-transparent text-slate-500 hover:text-brand-navy"
              }`}
              data-testid={`agency-tab-${t.key}`}
            >
              <Ico size={14}/> {t.label}
              {t.key === "reservations" && <span className="text-[10px] bg-slate-200/80 rounded-full px-1.5">{stats.reservation_count}</span>}
              {t.key === "prices" && <span className="text-[10px] bg-slate-200/80 rounded-full px-1.5">{prices.length}</span>}
            </button>
          );
        })}
      </div>

      {tab === "reservations" && (
        <div className="space-y-2" data-testid="agency-tab-reservations-content">
          {reservations.length === 0 && (
            <div className="rounded-2xl bg-white border border-brand-navy/10 p-10 text-center text-slate-500">
              Bu acentenin rezervasyonu yok.
            </div>
          )}
          {reservations.map((r) => {
            const heads = (r.adults||0) + (r.children||0) + (r.staff||0);
            const rooms = ["r1","r2","r3","r4","r5"].reduce((s,k)=>s+(r.rooms?.[k]||0), 0);
            const total = Number(r.total_price || r.agreed_room_price + r.agreed_meal_price || 0);
            return (
              <div key={r.id} className="rounded-2xl bg-white border border-brand-navy/10 hover:shadow-sm transition p-4 flex items-center gap-4 flex-wrap" data-testid={`agency-rez-${r.id}`}>
                <div className="flex-1 min-w-[220px]">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-xs text-brand-navy/70">{r.reservation_number}</span>
                    {r.is_revised && (
                      <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-amber-100 text-amber-800 border border-amber-200 inline-flex items-center gap-1">
                        <Sparkles size={9}/> Revize
                      </span>
                    )}
                    {(r.total_price || 0) > 0 && (
                      <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 border border-emerald-200 inline-flex items-center gap-1">
                        <CheckCircle2 size={9}/> Fiyat Kilitli
                      </span>
                    )}
                  </div>
                  <div className="font-medium text-brand-navy mt-1">{r.group_name || r.customer_phone || "—"}</div>
                  <div className="text-xs text-slate-500 mt-0.5">
                    {r.hotel_name && <span>{r.hotel_name} · </span>}
                    {formatTr(r.checkin)} → {formatTr(r.checkout)} ({r.nights}g)
                  </div>
                </div>
                <div className="text-xs text-slate-600 flex gap-3">
                  <span><b>{heads}</b> kişi</span>
                  <span><b>{rooms}</b> oda</span>
                </div>
                <div className="text-right">
                  <div className="text-[10px] uppercase text-slate-400">Toplam</div>
                  <div className="text-brand-orange-dark font-serif text-lg">{fmtSar(total)}</div>
                </div>
                <Link to={`/acente/rezervasyon/${r.id}`} className="text-xs px-3 py-1.5 rounded-full bg-brand-navy/5 hover:bg-brand-navy/10 text-brand-navy inline-flex items-center gap-1" data-testid={`agency-rez-detail-${r.id}`}>
                  <Eye size={12}/> Detay
                </Link>
              </div>
            );
          })}
        </div>
      )}

      {tab === "payments" && (
        <div className="space-y-3" data-testid="agency-tab-payments-content">
          <div className="rounded-2xl bg-gradient-to-br from-emerald-50 to-white border-2 border-emerald-200 p-5">
            <div className="text-xs uppercase tracking-widest text-emerald-800 font-semibold">Bakiye Özeti</div>
            <div className="mt-2 grid gap-3 sm:grid-cols-3">
              <div>
                <div className="text-[10px] text-slate-500 uppercase">Toplam Anlaşılan</div>
                <div className="font-serif text-2xl text-brand-navy">{fmtSar(stats.total_agreed)}</div>
              </div>
              <div>
                <div className="text-[10px] text-slate-500 uppercase">Toplanan</div>
                <div className="font-serif text-2xl text-emerald-700">{fmtSar(stats.total_collected)}</div>
              </div>
              <div>
                <div className="text-[10px] text-slate-500 uppercase">Kalan Bakiye</div>
                <div className={`font-serif text-2xl ${stats.balance > 0 ? "text-rose-700" : "text-slate-500"}`}>{fmtSar(stats.balance)}</div>
              </div>
            </div>
          </div>

          <div className="rounded-2xl bg-white border border-brand-navy/10 overflow-hidden">
            <div className="text-xs uppercase tracking-widest text-brand-navy/70 font-semibold px-5 pt-4">Tahsilat Kayıtları</div>
            <div className="p-4 overflow-x-auto">
              {paymentRows.length === 0 ? (
                <div className="text-slate-500 text-sm py-6 text-center">Henüz tahsil edilen ücret yok.</div>
              ) : (
                <table className="w-full text-sm" data-testid="agency-payments-table">
                  <thead className="text-xs uppercase text-slate-500 border-b border-slate-200">
                    <tr className="text-left">
                      <th className="py-2 px-2">Rezervasyon</th>
                      <th className="py-2 px-2">Giriş</th>
                      <th className="py-2 px-2">Tahsil Edildiği Tarih</th>
                      <th className="py-2 px-2">Tahsil Eden</th>
                      <th className="py-2 px-2 text-right">Tutar</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paymentRows.map((p) => (
                      <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`agency-payment-${p.id}`}>
                        <td className="py-2 px-2">
                          <div className="font-mono text-xs text-slate-500">{p.reservation_number}</div>
                          <div className="text-brand-navy">{p.group_name}</div>
                        </td>
                        <td className="py-2 px-2">{formatTr(p.checkin)}</td>
                        <td className="py-2 px-2">{p.collected_at ? new Date(p.collected_at).toLocaleString("tr-TR") : "—"}</td>
                        <td className="py-2 px-2 text-slate-600">{p.collected_by_name || "—"}</td>
                        <td className="py-2 px-2 text-right text-emerald-700 font-semibold">{fmtSar(p.collected_amount)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      {tab === "prices" && (
        <div className="space-y-4" data-testid="agency-tab-prices-content">
          <div className="rounded-2xl bg-blue-50 border border-blue-200 p-4 text-sm text-blue-900 space-y-1" data-testid="agency-prices-info">
            <div><b>Bu acenteye özel otel fiyatları:</b> Yeni rezervasyon oluşturulurken otel adı yazıldığında birim fiyatlar otomatik dolar. Operasyon değiştirebilir.</div>
            <div className="text-xs text-blue-800/80">
              🔒 Fiyat değişikliği <b>yalnızca yeni rezervasyonlara</b> uygulanır — mevcut kayıtların fiyatları sabit kalır (kilitli fiyat kural olarak korunur).
            </div>
          </div>

          <div className="flex justify-end">
            <Button
              onClick={() => setBatchOpen(true)}
              className="bg-brand-navy text-white hover:bg-brand-navy/90"
              data-testid="agency-price-batch-btn"
            >
              <Plus size={14} className="mr-1"/> Toplu Fiyat Ekle
            </Button>
          </div>

          <div id="agency-price-form" className="rounded-2xl bg-white border border-brand-navy/10 p-5 grid gap-3" data-testid="agency-price-form">
            <div className="flex items-center gap-2">
              <h3 className="font-serif text-lg text-brand-navy">{editingId ? "Fiyat Düzenle" : "Yeni Otel Fiyatı"}</h3>
              {editingId && <button onClick={() => { setEditingId(null); setPriceForm({ hotel_name: "", room_unit_price: 0, meal_unit_price: 0, note: "" }); }} className="text-xs text-slate-500 hover:text-brand-orange">Vazgeç</button>}
            </div>
            <div className="grid gap-3 sm:grid-cols-3">
              <div className="sm:col-span-3">
                <Label>Otel Adı *</Label>
                <Input value={priceForm.hotel_name} onChange={(e) => setPriceForm((f) => ({ ...f, hotel_name: e.target.value }))} placeholder="Diyar Ajyad" data-testid="agency-price-hotel" disabled={!!editingId}/>
                {editingId && <div className="text-[10px] text-slate-400 mt-1">Mevcut kaydı silip yenisini eklemek için önce silin.</div>}
              </div>
              <div>
                <Label>Oda Birim Fiyatı <span className="text-xs text-slate-400">(oda/gece SAR)</span></Label>
                <Input type="number" min="0" step="0.01" value={priceForm.room_unit_price} onChange={(e) => setPriceForm((f) => ({ ...f, room_unit_price: Number(e.target.value) }))} data-testid="agency-price-room" {...numberFieldProps} />
              </div>
              <div>
                <Label>Yemek Birim Fiyatı <span className="text-xs text-slate-400">(kişi/gün SAR)</span></Label>
                <Input type="number" min="0" step="0.01" value={priceForm.meal_unit_price} onChange={(e) => setPriceForm((f) => ({ ...f, meal_unit_price: Number(e.target.value) }))} data-testid="agency-price-meal" {...numberFieldProps} />
              </div>
              <div>
                <Label>Not</Label>
                <Input value={priceForm.note} onChange={(e) => setPriceForm((f) => ({ ...f, note: e.target.value }))} placeholder="Opsiyonel" data-testid="agency-price-note"/>
              </div>
            </div>
            <div className="flex justify-end">
              <Button onClick={submitPrice} disabled={savingPrice} className="bg-brand-orange text-white" data-testid="agency-price-save">
                <Plus size={14} className="mr-1"/> {savingPrice ? "Kaydediliyor..." : (editingId ? "Güncelle" : "Ekle")}
              </Button>
            </div>
          </div>

          <div className="rounded-2xl bg-white border border-brand-navy/10 overflow-hidden">
            <div className="text-xs uppercase tracking-widest text-brand-navy/70 font-semibold px-5 pt-4">Kayıtlı Otel Fiyatları</div>
            <div className="p-4 overflow-x-auto">
              {prices.length === 0 ? (
                <div className="text-slate-500 text-sm py-6 text-center">Bu acente için henüz otel fiyatı tanımlanmamış.</div>
              ) : (
                <table className="w-full text-sm" data-testid="agency-prices-table">
                  <thead className="text-xs uppercase text-slate-500 border-b border-slate-200">
                    <tr className="text-left">
                      <th className="py-2 px-2">Otel</th>
                      <th className="py-2 px-2 text-right">Oda / Gece</th>
                      <th className="py-2 px-2 text-right">Yemek / Kişi / Gün</th>
                      <th className="py-2 px-2">Not</th>
                      <th className="py-2 px-2 text-right"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {prices.map((p) => (
                      <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`agency-price-row-${p.id}`}>
                        <td className="py-2 px-2 font-medium text-brand-navy">{p.hotel_name}</td>
                        <td className="py-2 px-2 text-right font-serif text-brand-navy">{fmtSar(p.room_unit_price)}</td>
                        <td className="py-2 px-2 text-right font-serif text-brand-navy">{fmtSar(p.meal_unit_price)}</td>
                        <td className="py-2 px-2 text-slate-500 text-xs">{p.note}</td>
                        <td className="py-2 px-2 text-right">
                          <button onClick={() => startEditPrice(p)} className="text-brand-orange hover:text-brand-orange-dark text-xs inline-flex items-center gap-1 mr-2" data-testid={`agency-price-edit-${p.id}`}>
                            <Pencil size={11}/> Düzenle
                          </button>
                          <button onClick={() => setDeleteTarget(p)} className="text-rose-600 hover:text-rose-800 text-xs inline-flex items-center gap-1" data-testid={`agency-price-delete-${p.id}`}>
                            <Trash2 size={11}/> Sil
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      )}

      <AlertDialog open={!!deleteTarget} onOpenChange={(v) => !v && setDeleteTarget(null)}>
        <AlertDialogContent data-testid="agency-price-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Fiyatı Sil: {deleteTarget?.hotel_name}</AlertDialogTitle>
            <AlertDialogDescription>
              Bu acente için "{deleteTarget?.hotel_name}" oteline tanımlı fiyat silinecek.
              Yeni rezervasyonlarda artık otomatik dolmaz.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="agency-price-delete-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeletePrice} className="bg-rose-600 hover:bg-rose-700 focus:ring-rose-600" data-testid="agency-price-delete-confirm">Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog open={batchOpen} onOpenChange={setBatchOpen}>
        <AlertDialogContent className="max-w-3xl" data-testid="agency-price-batch-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Toplu Fiyat Ekle · {agency?.name}</AlertDialogTitle>
            <AlertDialogDescription>
              Aynı acente için birden fazla otel fiyatını tek ekranda kaydedin. Aynı otel adına
              tekrar girerseniz mevcut fiyat güncellenir. Otel adı boş bırakılan satırlar atlanır.
            </AlertDialogDescription>
          </AlertDialogHeader>

          <div className="max-h-[420px] overflow-y-auto space-y-2 py-2" data-testid="agency-price-batch-rows">
            <div className="grid grid-cols-[minmax(150px,1.5fr)_minmax(90px,1fr)_minmax(90px,1fr)_minmax(120px,1fr)_36px] gap-2 items-center px-1 text-[10px] uppercase text-slate-500 font-semibold">
              <div>Otel Adı</div>
              <div>Oda / Gece</div>
              <div>Yemek / Kişi / Gün</div>
              <div>Not</div>
              <div></div>
            </div>
            {batchRows.map((row, idx) => (
              <div
                key={idx}
                className="grid grid-cols-[minmax(150px,1.5fr)_minmax(90px,1fr)_minmax(90px,1fr)_minmax(120px,1fr)_36px] gap-2 items-center"
                data-testid={`agency-price-batch-row-${idx}`}
              >
                <Input
                  value={row.hotel_name}
                  onChange={(e) => updateBatchRow(idx, "hotel_name", e.target.value)}
                  placeholder="Diyar Ajyad"
                  data-testid={`agency-price-batch-hotel-${idx}`}
                />
                <Input
                  type="number" min="0" step="0.01"
                  value={row.room_unit_price}
                  onChange={(e) => updateBatchRow(idx, "room_unit_price", Number(e.target.value))}
                  data-testid={`agency-price-batch-room-${idx}`}
                  {...numberFieldProps}
                />
                <Input
                  type="number" min="0" step="0.01"
                  value={row.meal_unit_price}
                  onChange={(e) => updateBatchRow(idx, "meal_unit_price", Number(e.target.value))}
                  data-testid={`agency-price-batch-meal-${idx}`}
                  {...numberFieldProps}
                />
                <Input
                  value={row.note}
                  onChange={(e) => updateBatchRow(idx, "note", e.target.value)}
                  placeholder="Opsiyonel"
                  data-testid={`agency-price-batch-note-${idx}`}
                />
                <button
                  type="button"
                  onClick={() => removeBatchRow(idx)}
                  disabled={batchRows.length <= 1}
                  className="h-9 w-9 rounded-full text-rose-600 hover:bg-rose-50 disabled:opacity-30 disabled:cursor-not-allowed flex items-center justify-center"
                  title="Satırı sil"
                  data-testid={`agency-price-batch-remove-${idx}`}
                >
                  <Trash2 size={14}/>
                </button>
              </div>
            ))}
          </div>

          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
            <button
              type="button"
              onClick={addBatchRow}
              className="text-sm text-brand-navy hover:text-brand-orange inline-flex items-center gap-1"
              data-testid="agency-price-batch-add-row"
            >
              <Plus size={14}/> Satır Ekle
            </button>
            <div className="text-xs text-slate-500">
              {batchRows.filter((r) => (r.hotel_name || "").trim()).length} otel kaydedilecek
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel data-testid="agency-price-batch-cancel">Vazgeç</AlertDialogCancel>
            <AlertDialogAction
              onClick={submitBatch}
              disabled={batchBusy}
              className="bg-brand-orange hover:bg-brand-orange-dark focus:ring-brand-orange"
              data-testid="agency-price-batch-save"
            >
              {batchBusy ? "Kaydediliyor..." : "Hepsini Kaydet"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

const TONES = {
  navy: { bg: "bg-brand-navy/5", border: "border-brand-navy/20", icon: "bg-brand-navy/10 text-brand-navy", num: "text-brand-navy" },
  orange: { bg: "bg-brand-orange/5", border: "border-brand-orange/30", icon: "bg-brand-orange/15 text-brand-orange-dark", num: "text-brand-orange-dark" },
  emerald: { bg: "bg-emerald-50", border: "border-emerald-200", icon: "bg-emerald-100 text-emerald-700", num: "text-emerald-700" },
  rose: { bg: "bg-rose-50", border: "border-rose-200", icon: "bg-rose-100 text-rose-700", num: "text-rose-700" },
  slate: { bg: "bg-slate-50", border: "border-slate-200", icon: "bg-slate-100 text-slate-500", num: "text-slate-500" },
};

function KpiCard({ icon, label, value, tone = "navy", testid }) {
  const t = TONES[tone] || TONES.navy;
  return (
    <div className={`rounded-2xl border ${t.border} ${t.bg} p-4 flex items-center gap-3`} data-testid={testid}>
      <div className={`w-10 h-10 rounded-xl ${t.icon} flex items-center justify-center flex-shrink-0`}>{icon}</div>
      <div className="min-w-0">
        <div className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{label}</div>
        <div className={`font-serif text-xl leading-none mt-0.5 ${t.num}`}>{value}</div>
      </div>
    </div>
  );
}
